import type { SavedResearch } from '../../../../services/types';
import { HeaderSettings } from '../../Headerfiles/types';
import { generatePdfHeader, getFontImports } from '../../Headerfiles/pdfHeaderGenerator';

const getPrintableStyles = () => `
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Times+New+Roman&display=swap');
        body { 
            font-family: 'Times New Roman', Times, serif; 
            font-size: 11pt;
            line-height: 1.6;
            color: #333;
            margin: 0;
            padding: 40px;
            max-width: 800px;
            margin-left: auto;
            margin-right: auto;
        }
        .default-header { 
            text-align: center; 
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
            margin-bottom: 25px; 
        }
        .default-header h1 { font-size: 18pt; margin: 0; font-weight: bold; }
        .default-header .details { 
            font-size: 10pt; 
            margin-top: 10px; 
            color: #666; 
        }
        .summary-section { 
            background-color: #f8f9fa; 
            padding: 15px; 
            border-left: 4px solid #0056b3;
            margin-bottom: 25px;
        }
        .summary-section h2 { 
            font-size: 14pt; 
            margin: 0 0 10px 0; 
            color: #0056b3; 
        }
        .category-section { 
            margin-bottom: 25px; 
            page-break-inside: avoid; 
        }
        .category-section h3 { 
            font-size: 13pt; 
            margin: 0 0 10px 0; 
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
        }
        .category-content { 
            white-space: pre-wrap; 
            text-align: justify;
        }
        .sources-section { 
            margin-top: 30px; 
            padding-top: 20px;
            border-top: 2px solid #333;
        }
        .sources-section h2 { 
            font-size: 14pt; 
            margin-bottom: 15px; 
        }
        .source-item { 
            margin-bottom: 10px; 
            font-size: 10pt;
            padding-left: 20px;
            text-indent: -20px;
        }
        .source-link { 
            color: #0056b3; 
            text-decoration: none; 
        }
        @media print {
            body { margin: 20mm; padding: 0; }
        }
    </style>
`;

const createPrintableResearchContent = (research: SavedResearch, headerSettings?: HeaderSettings) => {
    const { subject, topics, areaOfFocus, result, createdAt } = research;
    const date = new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Use centralized header generator
    const customHeader = generatePdfHeader(headerSettings);
    let headerHTML = '';

    if (customHeader) {
        // Custom header with research info below it
        // Note: margin-bottom is controlled by the header's separator.marginBottom setting
        headerHTML = customHeader + `
            <div style="text-align: center;">
                <h2 style="font-size: 16pt; margin: 0;">Research Report: ${topics.join(', ')}</h2>
                <div style="font-size: 10pt; color: #666;">Generated: ${date}</div>
            </div>
        `;
    } else {
        // Default header (no custom header)
        headerHTML = `
            <div class="default-header">
                <h1>Research Report: ${topics.join(', ')}</h1>
                <div class="details">
                    <div><strong>Subject:</strong> ${subject}</div>
                    ${areaOfFocus ? `<div><strong>Focus Area:</strong> ${areaOfFocus}</div>` : ''}
                    <div><strong>Generated:</strong> ${date}</div>
                </div>
            </div>
        `;
    }

    let content = headerHTML;

    if (result.summary) {
        content += `
            <div class="summary-section">
                <h2>Executive Summary</h2>
                <div>${result.summary}</div>
            </div>
        `;
    }

    if (result.categories && result.categories.length > 0) {
        result.categories.forEach((category, index) => {
            content += `
                <div class="category-section">
                    <h3>${index + 1}. ${category.title}</h3>
                    <div class="category-content">${category.content}</div>
                </div>
            `;
        });
    }

    if (result.sources && result.sources.length > 0) {
        content += `
            <div class="sources-section">
                <h2>References & Sources</h2>
        `;
        result.sources.forEach((source, index) => {
            content += `
                <div class="source-item">
                    ${index + 1}. <a href="${source.web.uri}" class="source-link" target="_blank">${source.web.title || source.web.uri}</a>
                </div>
            `;
        });
        content += `</div>`;
    }

    return content;
};

export const exportResearchPDF = (research: SavedResearch, headerSettings?: HeaderSettings) => {
    const content = createPrintableResearchContent(research, headerSettings);
    const styles = getPrintableStyles();
    const fontImports = getFontImports(headerSettings);

    const printWindow = window.open('', '', 'height=800,width=800');
    if (!printWindow) {
        alert('Please allow popups to export as PDF');
        return;
    }

    printWindow.document.write(`<html><head><title>Research Export</title>${styles}${fontImports}</head><body>${content}</body></html>`);
    printWindow.document.close();

    // Wait for content (including images) to load before printing
    const hasLogo = headerSettings?.isEnabled && headerSettings.logo.url;
    setTimeout(() => {
        printWindow.print();
    }, hasLogo ? 1500 : 500);
};

export const exportResearchWord = (research: SavedResearch, headerSettings?: HeaderSettings) => {
    const content = createPrintableResearchContent(research, headerSettings);
    const styles = getPrintableStyles();
    const header = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Research Export</title>${styles}</head><body>`;
    const footer = "</body></html>";
    const sourceHTML = header + content + footer;

    const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
    const fileDownload = document.createElement("a");
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    const filename = `research-${research.subject}-${research.topics.join('-').replace(/\s+/g, '-')}-${Date.now()}.doc`;
    fileDownload.download = filename;
    fileDownload.click();
    document.body.removeChild(fileDownload);
};

export const exportBibliography = (sources: Array<{ web: { uri: string; title: string } }>, format: 'APA' | 'MLA' | 'Chicago' | 'Harvard') => {
    let content = `Bibliography (${format} Format)\n\n`;

    sources.forEach((source, index) => {
        const title = source.web.title || 'Untitled';
        const url = source.web.uri;
        const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

        switch (format) {
            case 'APA':
                content += `${index + 1}. ${title}. Retrieved ${date}, from ${url}\n\n`;
                break;
            case 'MLA':
                content += `${index + 1}. "${title}." Web. ${date}. <${url}>\n\n`;
                break;
            case 'Chicago':
                content += `${index + 1}. "${title}." Accessed ${date}. ${url}.\n\n`;
                break;
            case 'Harvard':
                content += `${index + 1}. ${title} (${new Date().getFullYear()}). Available at: ${url} (Accessed: ${date}).\n\n`;
                break;
        }
    });

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bibliography-${format.toLowerCase()}-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
